import { defineElement } from "@acng/frontend-bounty";
import { createMap, existsOnMap, getFromMap, putOnMap } from "@acng/frontend-bounty/map";
import { pushToArray } from "@acng/frontend-bounty/array";
import { Error, isPrototypeOf } from "@acng/frontend-bounty/types";
import { REGISTRY_ELEMENT, RESOURCE_HIDDEN } from "../model/resource.js";
import { ResourceUtility } from "../model/utility/resource.js";
import { Widget } from "../model/widget.js";
import { isInjectable } from "./angular.js";
const MODULE = "stargazer/element/registry";
const namespace_ = Symbol();
const constructor_ = Symbol();
const loading_ = Symbol();
const loader_ = Symbol();
const setup_ = Symbol();
/**
 * @typedef {import("../model/resource.js").ResourceElement} ResourceElement
 */
/**
 * @template {HTMLElement} T
 * @callback CustomElementLoader
 * @param {string} name
 * @returns {Promise<new () => T>}
 */
/**
 * @template {HTMLElement} T
 * @callback SetupCallback
 * @param {boolean} first
 * @param {new () => T} constructor
 * @returns {Promise<void>}
 */
/**
 * @template {HTMLElement} [T = HTMLElement]
 * @typedef {{
 *   [namespace_]: ResourceElement | null;
 *   [constructor_]?: (new () => T);
 *   [loader_]?: CustomElementLoader<T>;
 *   [setup_]?: SetupCallback<T>;
 *   [loading_]?: Promise<void>;
 * }} Entry
 */
/**
 * @type {Map<string, Entry>}
 */
const registry = createMap();
/**
 * @param {string} name
 * @param {HTMLTemplateElement | ResourceUtility | null} namespace
 * @param {(new () => HTMLElement)} [constructor]
 * @param {CustomElementLoader<HTMLElement>} [loader]
 * @param {SetupCallback<HTMLElement>} [setup]
 */
const put = (name, namespace, constructor, loader, setup) => putOnMap(registry, name, {
    [namespace_]: namespace instanceof ResourceUtility ? namespace[REGISTRY_ELEMENT] : namespace,
    [constructor_]: constructor,
    [loader_]: loader,
    [setup_]: setup,
});
/**
 * @template {HTMLElement} T
 * @param {HTMLTemplateElement | ResourceUtility | null} namespace
 * @param {string} name
 * @param {new () => T} [constructor]
 */
export const defineCustomElement = (namespace, name, constructor) => {
    DEBUG: if (existsOnMap(registry, name)) {
        throw Error(`${MODULE} ${name}`);
    }
    if (constructor) {
        defineElement(name, constructor);
    }
    put(name, namespace, constructor);
};
/**
 * @param {HTMLTemplateElement | ResourceUtility | null} namespace
 * @param {string} name
 * @param {CustomElementLoader<HTMLElement>} loader
 * @param {SetupCallback<HTMLElement>} [setup]
 */
export const defineCustomElementLoader = (namespace, name, loader, setup) => {
    DEBUG: if (existsOnMap(registry, name)) {
        throw Error(`${MODULE} ${name}`);
    }
    put(name, namespace, undefined, loader, setup);
};
/**
 * @param {string} name
 * @param {Promise<any>[]} promises
 */
export const setupCustomElement = (name, promises) => {
    // This check is only required for the isInjectable stuff below.
    if (!/^onsw-/.test(name)) {
        return true;
    }
    const entry = getFromMap(registry, name);
    if (!entry) {
        // An element not in the registry SHOULD be left allowed.
        // BUT return false if it is also not injectable via angularjs.
        // For this the naming convention above is required!
        return isInjectable(name);
        // return true;
    }
    const { [namespace_]: feature, [constructor_]: constructor, [loader_]: loader, [setup_]: setup } = entry;
    if (feature?.[RESOURCE_HIDDEN]) {
        return false;
    }
    if (constructor && setup) {
        pushToArray(promises, (entry[loading_] ??= (async () => {
            await setup(false, constructor);
            delete entry[loading_];
        })()));
    }
    if (loader) {
        pushToArray(promises, (entry[loading_] ??= (async () => {
            const constructor = await loader(name);
            if (isPrototypeOf(Widget, constructor)) {
                await constructor.load(name);
            }
            await setup?.(true, constructor);
            defineElement(name, constructor);
            delete entry[loading_];
            delete entry[loader_];
            entry[constructor_] = constructor;
        })()));
    }
    return true;
};
/**
 * @param {string} name
 */
export const createCustomWidget = async (name) => {
    /**
     * @type {Array<Promise<any>>}
     */
    const promises = [];
    if (!setupCustomElement(name, promises)) {
        return null;
    }
    await promises[0];
    return document.createElement(name);
};
